import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FormulairesService, Formulaire, FormulaireFile } from '../formulaires.service';

@Component({
  selector: 'app-formulaires-list',
  standalone: false,
  templateUrl: './formulaires-list.component.html',
  styleUrls: ['./formulaires-list.component.scss'],
})
export class FormulairesListComponent implements OnInit, OnDestroy {
  groups = signal<Record<string, Formulaire[]>>({});
  isLoading = signal(true);
  searchQuery = signal('');
  selectedType = signal('');
  downloadingFormulaire = signal<{ id: number; type: string } | null>(null);

  readonly typeLabels: Record<string, string> = {
    PLAY: 'Joueurs', COACH: 'Entraîneurs', MANA: 'Dirigeants', CLUB: 'Clubs', COMP: 'Compétitions',
    REFE: 'Arbitres', ARBITRE: 'Arbitres',
  };

  readonly Object = Object;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(private service: FormulairesService, private router: Router) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.loadFormulaires());
    this.loadFormulaires();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  loadFormulaires(): void {
    this.isLoading.set(true);
    this.service.getFormulaires(this.searchQuery(), this.selectedType()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.groups.set(res.data || {}); this.isLoading.set(false); },
      error: () => { this.groups.set({}); this.isLoading.set(false); }
    });
  }

  onSearchChange(value: string): void { this.searchQuery.set(value); this.searchSubject.next(value); }
  onTypeChange(value: string): void { this.selectedType.set(value); this.loadFormulaires(); }
  onAddNew(): void { this.router.navigate(['/federation/formulaires/new']); }
  onViewDetail(id: number): void { this.router.navigate(['/federation/formulaires', id]); }

  getFileByExtension(files: FormulaireFile[] | undefined, ext: string): FormulaireFile | undefined {
    return files?.find(f => f.file_extension?.toUpperCase() === ext.toUpperCase());
  }

  getDownloadName(formulaire: Formulaire, ext: string): string {
    const name = (formulaire.formulaire_nom_fr || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9_\- ]/g, '').trim().replace(/\s+/g, '_');
    return `${formulaire.formulaire_code || 'formulaire'}_${name}.${ext.toLowerCase()}`;
  }

  async downloadFile(formulaire: Formulaire, file: FormulaireFile, type: string): Promise<void> {
    this.downloadingFormulaire.set({ id: formulaire.formulaire_id, type });
    try {
      const response = await fetch(file.file_link);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.getDownloadName(formulaire, file.file_extension);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Download error:', e); }
    finally { this.downloadingFormulaire.set(null); }
  }
}
